import { useMemo } from 'react';

import { tokens } from '@cast/design-system';
import { SeeMoreButton } from '@cast/features-cluster-ui';
import { bytesToGiBiBytes, dashesOrNum } from '@cast/utils';
import {
  ActionRequired,
  useNetworkSummaryQuery,
} from '@cast/split-me/features/cluster/cost-report';

import { SegmentedMetricCard } from '../_components/SegmentedMetricCard';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { NETWORK_TRAFFIC_SORT } from '../_utils/dashboardListNavigation';
import { useNetworkCostAdjustedDateRange } from './_hooks/useNetworkCostAdjustedDateRange';

const formatTraffic = (value: number | undefined): string =>
  value === undefined
    ? '--'
    : String(dashesOrNum({ value: bytesToGiBiBytes(value), roundBy: 2 }));

const EGRESS_COLOR = tokens.primitives.colors.blue['600'];
const INGRESS_COLOR = tokens.primitives.colors.blue['200'];

export const NetworkTrafficCard = () => {
  const { adjustedDateRange } = useNetworkCostAdjustedDateRange();
  const {
    data,
    error,
    refetch,
    isLoading,
    isFetching,
    isCapabilitiesLoading,
    hasNetworkAgent,
  } = useNetworkSummaryQuery({ adjustedDateRange });
  const { navigateToNetworkList } = useInventoryWorkloadsDashboardNavigation();

  const seeMore = (
    <SeeMoreButton
      onClick={() => navigateToNetworkList(NETWORK_TRAFFIC_SORT)}
    />
  );

  const segments = useMemo(
    () => [
      {
        label: 'Egress',
        value: data?.traffic.egress ?? 0,
        color: EGRESS_COLOR,
        testId: 'network-traffic-egress',
      },
      {
        label: 'Ingress',
        value: data?.traffic.ingress ?? 0,
        color: INGRESS_COLOR,
        testId: 'network-traffic-ingress',
      },
    ],
    [data]
  );

  if (!isCapabilitiesLoading && !hasNetworkAgent) {
    return (
      <WidgetLayout
        title="Network traffic"
        testId="network-traffic-card"
        headerDivider={false}
        headerEndContent={seeMore}
        slotProps={{ header: { py: 12 } }}
      >
        <ActionRequired />
      </WidgetLayout>
    );
  }

  return (
    <SegmentedMetricCard
      title="Network traffic"
      total={data?.traffic.total}
      formatValue={formatTraffic}
      unit="GiB"
      segments={segments}
      testId="network-traffic-card"
      metricTestId="network-traffic-total"
      barTestId="network-traffic-bar"
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      refresh={refetch}
      headerEndContent={seeMore}
    />
  );
};
